import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Plus, Pencil, Trash2, Loader, X, Search, Layers,
} from "lucide-react";
import {
  lmsGetBatches, lmsCreateBatch, lmsUpdateBatch, lmsDeleteBatch,
  lmsGetSchoolClasses,
} from "../api/lmsApi";
import "../lms.css";

const getSchoolId = () => {
  try { return JSON.parse(localStorage.getItem("userData") || "{}").admin_id || null; }
  catch { return null; }
};

/* ── Confirm Modal ── */
function ConfirmModal({ message, onConfirm, onCancel, loading }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 16, width: 420, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "32px 28px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
          <div style={{
            width: 60, height: 60, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Trash2 size={26} color="#dc2626" />
          </div>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Confirm Delete</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.7 }}>{message}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, padding: "0 28px 24px", justifyContent: "center" }}>
          <button onClick={onCancel} disabled={loading} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading} style={{
            flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#dc2626,#b91c1c)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 6,
          }}>
            {loading ? <Loader size={13} className="ms2-spin" /> : <Trash2 size={14} />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Field ── */
function Field({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{label}</label>
      {children}
    </div>
  );
}
const inp = {
  padding: "9px 12px", borderRadius: 8, border: "1.5px solid #e5e7eb",
  fontSize: 13, color: "#111827", outline: "none", width: "100%", boxSizing: "border-box",
};

function mkForm() {
  return { name: "", class_id: "", section: "", description: "", max_students: "", start_date: "", end_date: "", status: "active" };
}

export default function Batches() {
  const schoolId = getSchoolId();

  const [batches, setBatches]     = useState([]);
  const [classes, setClasses]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch]       = useState("");
  const [filterClass, setFilterClass] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem]   = useState(null);
  const [form, setForm]           = useState(mkForm());

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  /* ── Load ── */
  const load = async () => {
    setLoading(true);
    try {
      const params = { school_id: schoolId };
      if (filterClass) params.class_id = filterClass;
      const res = await lmsGetBatches(params);
      setBatches(res.data.data || []);
    } catch {
      toast.error("Failed to load batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    lmsGetSchoolClasses(schoolId).then((r) => setClasses(r.data.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [filterClass]);

  /* ── Modal helpers ── */
  const openCreate = () => { setEditItem(null); setForm(mkForm()); setModalOpen(true); };
  const openEdit   = (b)  => {
    setEditItem(b);
    setForm({
      name:         b.name         || "",
      class_id:     b.class_id     || "",
      section:      b.section      || "",
      description:  b.description  || "",
      max_students: b.max_students || "",
      start_date:   b.start_date   ? b.start_date.slice(0, 10) : "",
      end_date:     b.end_date     ? b.end_date.slice(0, 10)   : "",
      status:       b.status       || "active",
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Batch name is required");
    setSubmitting(true);
    try {
      const payload = {
        school_id:   schoolId,
        name:        form.name.trim(),
        class_id:    form.class_id    || null,
        section:     form.section     || null,
        description: form.description || null,
        max_students: form.max_students || 0,
        start_date:  form.start_date  || null,
        end_date:    form.end_date    || null,
        status:      form.status      || "active",
      };
      if (editItem) {
        await lmsUpdateBatch(editItem.id, payload);
        toast.success("Batch updated successfully");
      } else {
        await lmsCreateBatch(payload);
        toast.success("Batch created successfully");
      }
      setModalOpen(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to save batch";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await lmsDeleteBatch(deleteTarget.id);
      toast.success("Batch deleted successfully");
      setDeleteTarget(null);
      load();
    } catch {
      toast.error("Failed to delete batch");
    } finally {
      setDeleting(false);
    }
  };

  /* ── Filtered list ── */
  const filtered = batches.filter((b) =>
    (b.name || b.batch_name || "").toLowerCase().includes(search.toLowerCase()) ||
    (b.class_name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: "linear-gradient(135deg,#6366f1,#4f46e5)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <Layers size={20} color="#fff" />
            </div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: 0 }}>Batch Management</h1>
              <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>Manage batches for your school</p>
            </div>
          </div>
        </div>
        <button onClick={openCreate} style={{
          display: "flex", alignItems: "center", gap: 7, padding: "9px 18px",
          background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff",
          border: "none", borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: "pointer",
        }}>
          <Plus size={15} /> New Batch
        </button>
      </div>

      {/* Toolbar — one row */}
      <div className="crs-filters" style={{ marginBottom: 20 }}>
        <div className="crs-search-wrap">
          <Search size={15} className="crs-search-icon" />
          <input
            className="crs-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search batches..."
          />
        </div>
        <select
          className="crs-filter-select"
          value={filterClass}
          onChange={(e) => setFilterClass(e.target.value)}
          style={{ flex: "0 0 180px", width: 180 }}
        >
          <option value="">All Classes</option>
          {classes.map((c) => (
            <option key={c.class_id} value={c.class_id}>{c.class_name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
          <Loader size={28} className="ms2-spin" color="#6366f1" />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 0", background: "#fff",
          borderRadius: 12, border: "1px solid #e5e7eb",
        }}>
          <Layers size={40} color="#d1d5db" style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>No batches found</div>
          <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>Create your first batch to get started</div>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e5e7eb", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                {["#", "Batch Name", "Class", "Start Date", "End Date", "Description", "Actions"].map((h) => (
                  <th key={h} style={{
                    padding: "11px 14px", textAlign: "left", fontSize: 11, fontWeight: 700,
                    color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => (
                <tr key={b.id} style={{ borderBottom: "1px solid #f3f4f6" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "12px 14px", fontSize: 12, color: "#9ca3af" }}>{i + 1}</td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#111827" }}>{b.name}</div>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {b.class_name ? (
                      <span style={{
                        display: "inline-block", padding: "3px 10px", borderRadius: 20,
                        fontSize: 11, fontWeight: 600, background: "#ede9fe", color: "#6d28d9",
                      }}>{b.class_name}</span>
                    ) : (
                      <span style={{ color: "#9ca3af", fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12, color: "#374151" }}>
                    {b.start_date ? new Date(b.start_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12, color: "#374151" }}>
                    {b.end_date ? new Date(b.end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12, color: "#6b7280", maxWidth: 220 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {b.description || "—"}
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => openEdit(b)} style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
                        borderRadius: 6, border: "1.5px solid #e5e7eb", background: "#fff",
                        fontSize: 11, fontWeight: 600, color: "#374151", cursor: "pointer",
                      }}>
                        <Pencil size={11} /> Edit
                      </button>
                      <button onClick={() => setDeleteTarget(b)} style={{
                        display: "flex", alignItems: "center", gap: 4, padding: "5px 10px",
                        borderRadius: 6, border: "1.5px solid #fecaca", background: "#fef2f2",
                        fontSize: 11, fontWeight: 600, color: "#dc2626", cursor: "pointer",
                      }}>
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ padding: "10px 14px", borderTop: "1px solid #f3f4f6", fontSize: 12, color: "#9ca3af" }}>
            {filtered.length} batch{filtered.length !== 1 ? "es" : ""}
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {modalOpen && (
        <div className="ms2-modal-overlay" onClick={() => setModalOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: "#fff", borderRadius: 16, width: 520, maxWidth: "94vw",
            boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
          }}>
            {/* Gradient header bar */}
            <div style={{ height: 4, background: "linear-gradient(90deg,#6366f1,#4f46e5)" }} />

            {/* Modal header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "20px 24px 16px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8, background: "#ede9fe",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Layers size={18} color="#6d28d9" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                    {editItem ? "Edit Batch" : "New Batch"}
                  </div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>
                    {editItem ? "Update batch details" : "Fill in the details below"}
                  </div>
                </div>
              </div>
              <button onClick={() => setModalOpen(false)} style={{
                background: "#f3f4f6", border: "none", borderRadius: 8, padding: 6,
                cursor: "pointer", display: "flex", alignItems: "center",
              }}>
                <X size={15} color="#374151" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: "0 24px 20px", display: "flex", flexDirection: "column", gap: 14 }}>

                <Field label="Batch Name *">
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Batch A - Morning"
                    required
                    style={inp}
                  />
                </Field>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Class">
                    <select
                      value={form.class_id}
                      onChange={(e) => setForm({ ...form, class_id: e.target.value })}
                      style={inp}
                    >
                      <option value="">— Select Class —</option>
                      {classes.map((c) => (
                        <option key={c.class_id} value={c.class_id}>{c.class_name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Section">
                    <input
                      value={form.section}
                      onChange={(e) => setForm({ ...form, section: e.target.value })}
                      placeholder="e.g. A"
                      style={inp}
                    />
                  </Field>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Start Date">
                    <input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                      style={inp}
                    />
                  </Field>
                  <Field label="End Date">
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                      style={inp}
                    />
                  </Field>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <Field label="Max Students">
                    <input
                      type="number"
                      min="0"
                      value={form.max_students}
                      onChange={(e) => setForm({ ...form, max_students: e.target.value })}
                      placeholder="0 = unlimited"
                      style={inp}
                    />
                  </Field>
                  <Field label="Status">
                    <select
                      value={form.status}
                      onChange={(e) => setForm({ ...form, status: e.target.value })}
                      style={inp}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </Field>
                </div>

                <Field label="Description">
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Optional description..."
                    rows={2}
                    style={{ ...inp, resize: "vertical" }}
                  />
                </Field>

              </div>

              {/* Footer */}
              <div style={{
                display: "flex", gap: 10, padding: "16px 24px 20px",
                borderTop: "1px solid #f3f4f6",
              }}>
                <button type="button" onClick={() => setModalOpen(false)} style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
                }}>Cancel</button>
                <button type="submit" disabled={submitting} style={{
                  flex: 1, padding: "10px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: "none", background: "linear-gradient(135deg,#6366f1,#4f46e5)",
                  color: "#fff", cursor: "pointer", display: "flex", alignItems: "center",
                  justifyContent: "center", gap: 6,
                }}>
                  {submitting ? <Loader size={13} className="ms2-spin" /> : null}
                  {editItem ? "Update Batch" : "Create Batch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <ConfirmModal
          message={`Delete batch "${deleteTarget.name}"? This action cannot be undone.`}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={deleting}
        />
      )}
    </div>
  );
}
